import {
  initialize as launchDarklyInitialize,
  basicLogger as launchDarklyBasicLogger,
} from 'launchdarkly-js-client-sdk';

import { FeatureFlagList } from './featureFlagList';

import type {
  LDClient,
  LDFlagSet,
  LDContext,
} from 'launchdarkly-js-client-sdk';

type PropertyIdObj = {
  name?: string;
  id?: string | number;
};

type FeatureFlagEvent = 'change';
type FeatureFlagEventCallback = () => void;

/**
 * LaunchDarkly feature flag initializer and handler.
 */
export class FeatureFlagManager<T extends string = FeatureFlagList> {
  /**
   * Singleton storage. Only one FeatureFlagManager instance can exist, and it's
   * stored here. All instantiations of this class after the 1st will return
   * this instance, with the properties of the given enum merged with the existing
   * FFManInstance.validFlags object.
   */
  static FFManInstance: any;

  private client: LDClient | undefined;
  private data: LDFlagSet = {};

  /**
   * Store the enum that maps reference values to feature flag strings/names.
   */
  private validFlags: Record<T, string>;

  private events:
    | Record<string, never>
    | Record<FeatureFlagEvent, FeatureFlagEventCallback[]> = {};

  private overrideData: Record<T, boolean | undefined> = {} as Record<
    T,
    boolean | undefined
  >;

  private apiKey: string | undefined;
  /**
   * Purpose: add custom feature flag typings to the FeatureFlagManager.
   *
   * Merges the given enum with the existing enum, so the featureFlagManager
   * object has the correct typings in the location it's being used.
   *
   * WARNING
   * -------
   * This will not function until initialize is called, as the
   * LaunchDarkly client is not created until then.
   */
  constructor(flagEnum: Record<string, T>) {
    // Return pre-existing instance if it exists, and merge the enums with allowed feature flags.
    if (FeatureFlagManager.FFManInstance) {
      FeatureFlagManager.FFManInstance.validFlags = {
        ...FeatureFlagManager.FFManInstance.validFlags,
        ...flagEnum,
      };
      return FeatureFlagManager.FFManInstance as typeof this;
    }

    // Construct new instance if none exists.
    FeatureFlagManager.FFManInstance = this;
    FeatureFlagManager.FFManInstance.validFlags = flagEnum;

    // Defines a initial object with all feature flags set to false.
    this.data = Object.values(flagEnum).reduce(
      (acc, flag) => ({ ...acc, [flag]: false }),
      {},
    ) as LDFlagSet;

    // Attach the feature flag manager to the window object for testing.
    window.__dev__ = window?.__dev__ ?? ({} as unknown as any); // eslint-disable-line no-underscore-dangle
    window.__dev__.featureFlagManager = this; // eslint-disable-line no-underscore-dangle
    return this;
  }

  /**
   * Check if the given feature flag is enabled, return true if so.
   *
   * @param {string} flag The feature flag to check.
   * @returns boolean
   */
  isEnabled = (flag: T): boolean =>
    this.overrideData[flag] !== undefined
      ? this.overrideData[flag]
      : this.data[flag];

  /**
   * Create an instance of the LaunchDarkly client, and initialize it with the
   * given API key and context (property). Also attaches a logger.
   *
   * WARNING: this only initializes once. If called again, the original client
   * will remain, and the API key (if different from the original) won't be updated.
   *
   * The API key is found on the LaunchDarkly webapp: find the 'more environment
   * options' button on the environment tag you're associating it with (such as
   * dev-ga or prod-ga), and select 'client-side id' to copy it.
   */
  initialize = (config: {
    /**
     * LaunchDarkly API Key for an environment (e.g. '63619de61f265e0d5a4baacd').
     */
    apiKey: string;
    /**
     * Object containing the numeric ID of a property (e.g. 83234), and
     * optionally the property's name.
     */
    property?: PropertyIdObj;
  }): Promise<void> =>
    new Promise((resolve, reject) => {
      // Exit and warn user if client is already initialized.
      if (this.client) {
        console.debug(
          `[FeatureFlagManager] LaunchDarkly client already initialized.`,
        );
        if (this.apiKey !== config.apiKey) {
          console.warn(
            `[FeatureFlagManager] API key mismatch between LaunchDarkly initialization` +
              ` attempts. The client is already using API key ${this.apiKey},` +
              ` so newly passed API key ${config.apiKey} will be ignored.`,
          );
        }
        resolve();
        return;
      }

      this.apiKey = config.apiKey;

      this.client = launchDarklyInitialize(
        config.apiKey,
        this.getLaunchDarklyContext(config.property),
        {
          logger: launchDarklyBasicLogger({ level: 'none' }),
          bootstrap: this.data,
        },
      );
      this.client.on('initialized', this.onReceiveFlagData);
      this.client.on('change', this.onReceiveFlagData);
      this.client.on('ready', resolve);
      this.client.on('failed', reject);
      this.client.on('error', reject);
    });

  /**
   * Replace a feature flag value with the given value.
   *
   * Example
   * -------
   * ```
   * featureFlagManager.override(FeatureFlagTest.AllowCobadgedCards, true);
   * ```
   * This will set the FeatureFlagTest.AllowCobadgedCards feature flag to true,
   * regardless of the value it's set to in LaunchDarkly.
   */
  override = (key: T, newVal: boolean | undefined) => {
    this.overrideData[key] = newVal;
    this.dispatchEvent('change');
  };

  /**
   * Revert all feature flags to their original values. Removes all overrides.
   */
  clearOverrides = () => {
    this.overrideData = {} as Record<T, boolean | undefined>;
    this.dispatchEvent('change');
  };

  /**
   * Register a callback to be called when the feature flag data changes.
   */
  on = (event: FeatureFlagEvent, callback: () => void) => {
    if (this.events[event] === undefined) this.events[event] = [];
    this.events[event].push(callback);
  };

  /**
   * Deregister a callback that was previously set to be called when the
   * associated event was triggered.
   *
   * Example
   * -------
   * If we initially ran `featureFlagManager.on('change', someFunction)`,
   * `someFunction` will run any time the feature flag data changes (i.e.
   * when the 'change' event is dispatched).
   *
   * If we later run:
   * ```
   * featureFlagManager.off('change', someFunction)
   * ```
   * then `someFunction` will no longer run when the feature flag data changes.
   */
  off = (event: FeatureFlagEvent, callback: FeatureFlagEventCallback) => {
    if (this.events[event] === undefined) return;
    const index = this.events[event].indexOf(callback);
    this.events[event].splice(index, 1);
  };

  /**
   * Run all callbacks registered for the given event.
   * e.g. used to notify all listeners that the feature flag data has changed,
   *
   * Example
   * -------
   * dispatchEvent('change') calls all listeners we previously registered for
   * the 'change' event, with the `featureFlagManager.on` method (unless
   * they've been deactivated by `featureFlagManager.off`).
   */
  private dispatchEvent = (event: FeatureFlagEvent) => {
    this.events[event]?.forEach(ev => ev?.());
  };

  private getLaunchDarklyContext = (property?: PropertyIdObj): LDContext => ({
    key: `property:${property?.id || 0}`,
    kind: 'user',
    name: property?.name || 'Anonymous Context',
  });

  /**
   * Replace stored feature flag collection with the one received from
   * LaunchDarkly.
   */
  private onReceiveFlagData = () => {
    this.data = this.client?.allFlags() || {};
  };
}
